import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { BalanceRegistrationInjection } from "features/balanceRegistration/di/BalanceRegistrationInjection";
import { BalanceRegistrationDetailEntity } from "features/balanceRegistration/domain/entities/BalanceRegistrationDetailEntity";
import {
  ExportPayload,
  FetchListInput,
} from "features/balanceRegistration/domain/entities/BalanceRegistrationPayload";
import { BalanceRegistrationRequestEntity } from "features/balanceRegistration/domain/entities/BalanceRegistrationRequestEntity";
import { BalanceRegistrationResponseEntity } from "features/balanceRegistration/domain/entities/BalanceRegistrationResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { DateFormat } from "utils/format/DateFormat";
import { strings } from "utils/localizedStrings";
import { IBalanceRegistrationObservedState } from "../interfaces/IBalanceRegistration";

class BalanceRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new BalanceRegistrationRequestEntity(),
  });
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchDetail = new BehaviorSubject<BalanceRegistrationRequestEntity>(
    new BalanceRegistrationRequestEntity(),
  );

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
  }
}

class BalanceRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<Blob | null>(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class BalanceRegistrationViewModel extends BasedObservedViewModel<
  BalanceRegistrationInput,
  BalanceRegistrationOutput,
  IBalanceRegistrationObservedState
> {
  private balanceRegistrationUseCase =
    BalanceRegistrationInjection.getBalanceRegistrationUseCase();

  constructor() {
    super(new BalanceRegistrationInput(), new BalanceRegistrationOutput());
  }

  protected override createObservedState(): IBalanceRegistrationObservedState {
    return {
      list: [],
      detail: null,
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      queryParams: new BalanceRegistrationRequestEntity({
        page: 0,
        limit: 10,
        ...DateFormat.getDateRange(0),
        service: ServiceEnum.ADD_BALANCE,
      }),
      summary: {},
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (input, guard) => {
      try {
        guard?.runIfLatest(() => {
          const pagination =
            input.kind === "tableChange"
              ? {
                  ...this.observedState.pagination,
                  page: input.page,
                  limit: input.limit,
                }
              : { ...this.observedState.pagination, page: 0, total: 0 };

          this.setObservedState({
            loading: true,
            list: input.kind === "params" ? this.observedState.list : [],
            summary: input.kind === "params" ? this.observedState.summary : {},
            pagination,
          });
        });

        let response;
        let queryParams: BalanceRegistrationRequestEntity | null = null;

        switch (input.kind) {
          case "search": {
            const result =
              await this.balanceRegistrationUseCase.getBalanceRegistrationListFromSearch(
                input.rawFormValues,
                input.context,
              );
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "tableChange": {
            const result =
              await this.balanceRegistrationUseCase.getBalanceRegistrationListWithTableChange(
                input.prevParams,
                input.rawFormValues,
                input.page,
                input.limit,
              );
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "redo": {
            const result =
              await this.balanceRegistrationUseCase.getBalanceRegistrationListRedo();
            response = result.list;
            queryParams = result.params;
            break;
          }
          case "params":
          default:
            response =
              await this.balanceRegistrationUseCase.getBalanceRegistrationList(
                input.params,
              );
            queryParams = input.params;
            break;
        }

        guard?.runIfLatest(() => {
          this.setObservedState({
            list: (response?.data ?? []).map(
              (item) => new BalanceRegistrationResponseEntity(item),
            ),
            pagination: response?.pagination ?? {},
            summary: response?.total ?? {
              success: 0,
              error: 0,
              other: 0,
              amount: 0,
            },
            ...(queryParams ? { queryParams } : {}),
          });
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.setObservedState({ list: [] }));
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      if (!params?.code) return;

      try {
        this.setObservedState({ detailLoading: true, detail: null });
        const response =
          await this.balanceRegistrationUseCase.getBalanceRegistrationDetail(
            params,
          );
        if (response?.data) {
          this.setObservedState({
            detail: new BalanceRegistrationDetailEntity(response.data),
          });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload, guard) => {
      if (!payload) return;

      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const response =
          await this.balanceRegistrationUseCase.exportBalanceRegistrationReportFromSearch(
            payload,
          );
        if (response?.data?.size < 1000) {
          this.output.onError.next({
            message: strings.GlobalMessage.ExcelError,
          });
          return;
        }
        if (response?.data) {
          this.output.onExportSuccess.next(response.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });
  }
}
